import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, type DropResult } from '@hello-pangea/dnd';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';

import {
  FullTalentQuestionDTO,
  FullTalentQuestionDTOType,
  OrganizationDTOTalentUniqueFieldsItem,
  useGetOrganizationBySlug
} from '@/api/generated';
import DeleteQuestionIllustration from '@/assets/icons/warning-red.svg?react';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { FieldPrefixProvider, useFieldPrefix } from '@/hooks/useFieldPrefix';
import { TALENT_REGISTRATION_FORM_TABS } from '@/root/linker';
import { shortId } from '@/utils/shortId';

import CollapsibleContent from './CollapsibleContent';
import { OTHER_OPTION_ID } from './constants';
import {
  TalentRegistrationFormQuestion,
  TalentRegistrationFormQuestionDraggable
} from './TalentRegistrationFormQuestion';
import { normalizeOptionOrder } from './utils';

interface TalentRegistrationFormContentProps {
  activeTab: string;
  languages: string[];
  activeLanguage: string;
  tabKeys: string[];
  organizationSlug?: string;
}

export interface TalentRegistrationFormInstance {
  addQuestion: (question: FullTalentQuestionDTO) => void;
  removeQuestion: (id: string) => void;
}

const getEmptyTranslation = () => ({ label: '' });

const TalentRegistrationFormContent = forwardRef(
  (
    { activeTab, languages, activeLanguage, tabKeys, organizationSlug }: TalentRegistrationFormContentProps,
    ref: ForwardedRef<TalentRegistrationFormInstance>
  ) => {
    const [questionToRemove, setQuestionToRemove] = useState<string | null>(null);
    const { control, watch, getValues, setValue } = useFormContext();
    const { getPrefixedFieldName, getPrefixedFieldValue } = useFieldPrefix();
    const { spacing } = useTheme();
    const { t, i18n } = useTranslation(['settings', 'talents']);
    const uniqueId = useMemo(() => `talent-registration-form-${crypto.randomUUID()}`, []);

    const { data: organization } = useGetOrganizationBySlug(
      organizationSlug!,
      { isFull: true },
      { query: { enabled: !!organizationSlug } }
    );

    const { idNumberIsIdentity, passportIsIdentity, phoneIsIdentity } = useMemo(
      () => ({
        idNumberIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.ID_NUMBER),
        passportIsIdentity: organization?.talentUniqueFields?.includes(
          OrganizationDTOTalentUniqueFieldsItem.PASSPORT_NUMBER
        ),
        phoneIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.PHONE)
      }),
      [organization?.talentUniqueFields]
    );

    const {
      fields: questionsList,
      append: onAppendQuestion,
      update: onUpdateQuestion
    } = useFieldArray({
      control,
      name: getPrefixedFieldName(activeTab)
    });

    const questions = watch(getPrefixedFieldName(activeTab)) as FullTalentQuestionDTO[] | undefined;
    const allQuestions = watch(tabKeys.map(key => getPrefixedFieldName(key)));
    const allQuestionIds = useMemo(
      () =>
        allQuestions.flatMap(
          (questionList: FullTalentQuestionDTO[] | undefined) => questionList?.map(({ id }) => id).filter(Boolean) || []
        ),
      [allQuestions]
    );

    const onRemoveQuestion = useCallback(
      (questionId: string) => {
        const questionIndex = questions?.findIndex(question => question.id === questionId) ?? -1;
        if (!questions?.[questionIndex]) return;

        onUpdateQuestion(questionIndex, {
          ...questions[questionIndex],
          isActive: false
        });
      },
      [onUpdateQuestion, questions]
    );

    const onRemoveQuestionRequest = useCallback((id: string) => setQuestionToRemove(id), []);

    useImperativeHandle(
      ref,
      () => ({
        addQuestion: onAppendQuestion,
        removeQuestion: onRemoveQuestion
      }),
      [onAppendQuestion, onRemoveQuestion]
    );

    const onQuestionRemoveConfirm = useCallback(() => {
      questionToRemove && onRemoveQuestion(questionToRemove!);
      setQuestionToRemove(null);
    }, [onRemoveQuestion, questionToRemove]);

    const onQuestionRemoveCancel = useCallback(() => setQuestionToRemove(null), []);

    const onCopyQuestion = useCallback(
      (questionId: string) => {
        const questionIndex = questions?.findIndex(question => question.id === questionId) ?? -1;
        if (!questions?.[questionIndex]) return;

        const values = getPrefixedFieldValue(getValues());
        const oldId = questions?.[questionIndex]?.id;
        const newId = shortId();

        onAppendQuestion(
          _cloneDeep({
            ...questions[questionIndex],
            id: newId
          })
        );

        languages.forEach(language =>
          setValue(
            getPrefixedFieldName(`translations.${language}.${newId}`),
            _get(values, getPrefixedFieldName(`translations.${language}.${oldId}`))
          )
        );
      },
      [getPrefixedFieldName, getPrefixedFieldValue, getValues, languages, onAppendQuestion, questions, setValue]
    );

    useEffect(() => {
      const { translations } = getPrefixedFieldValue(getValues());

      if (!languages?.length) return;

      const allTranslations = Object.fromEntries(
        languages?.map(languageKey => {
          const singleLanguageTranslations = Object.fromEntries(
            allQuestionIds.map(questionId => {
              const existingTranslation = translations?.[languageKey]?.[questionId!];

              return [questionId, existingTranslation || getEmptyTranslation()];
            })
          );

          return [languageKey, singleLanguageTranslations];
        }) || []
      );
      setValue(getPrefixedFieldName('translations'), allTranslations);
    }, [allQuestionIds.join(','), languages?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    const onAddQuestionOption = useCallback(
      (questionId: string, isOther: boolean = false) => {
        const values = getPrefixedFieldValue(getValues());
        const activeQuestions = values[activeTab];
        const updatedQuestions = activeQuestions.map((question: FullTalentQuestionDTO) => {
          if (question.id !== questionId) return question;

          return {
            ...question,
            optionValues: normalizeOptionOrder([
              ...(question.optionValues || []),
              isOther ? OTHER_OPTION_ID : shortId()
            ])
          };
        });
        setValue(getPrefixedFieldName(activeTab), updatedQuestions, { shouldDirty: true });
      },
      [activeTab, getPrefixedFieldName, getPrefixedFieldValue, getValues, setValue]
    );

    const onRemoveQuestionOption = useCallback(
      (questionId: string, optionId: string) => {
        const values = getPrefixedFieldValue(getValues());
        const activeQuestions = values[activeTab];
        const updatedQuestions = activeQuestions.map((question: FullTalentQuestionDTO) => {
          if (question.id !== questionId) return question;

          return {
            ...question,
            optionValues: question.optionValues?.filter(currentOptionId => currentOptionId !== optionId)
          };
        });
        setValue(getPrefixedFieldName(activeTab), updatedQuestions, { shouldDirty: true });
      },
      [activeTab, getPrefixedFieldName, getPrefixedFieldValue, getValues, setValue]
    );

    const onDragEnd = useCallback(
      (result: DropResult<string>) => {
        if (!result.destination) return;
        const updatedQuestionsList = [...questionsList];
        const [removed] = updatedQuestionsList.splice(result.source.index, 1);
        updatedQuestionsList.splice(result.destination.index, 0, removed);
        const updatedQuestions = updatedQuestionsList.map(
          ({ id }: { id: string }) => questions?.[questionsList.findIndex(item => item.id === id)]
        );
        setValue(getPrefixedFieldName(activeTab), updatedQuestions);
      },
      [activeTab, getPrefixedFieldName, questions, questionsList, setValue]
    );

    const registrationFormValues = useMemo(
      () => ({
        external: [
          {
            id: 'firstName',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: true,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          },
          {
            id: 'lastName',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: true,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          },
          {
            id: 'idNumber',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: idNumberIsIdentity ?? false,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          },
          {
            id: 'phone',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: phoneIsIdentity ?? false,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          },
          {
            id: 'passportNumber',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: passportIsIdentity ?? false,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          },
          {
            id: 'email',
            type: FullTalentQuestionDTOType.TEXT,
            isRequired: true,
            isTalentEditable: true,
            isActive: true,
            isKnockout: false
          }
        ],
        translations: {
          [i18n.language]: {
            firstName: {
              label: t('talents:layouts.talentEditModal.fields.firstName')
            },
            lastName: {
              label: t('talents:layouts.talentEditModal.fields.lastName')
            },
            idNumber: {
              label: t('talents:layouts.talentEditModal.fields.idNumber')
            },
            phone: {
              label: t('talents:layouts.talentEditModal.fields.phone')
            },
            passportNumber: {
              label: t('talents:layouts.talentEditModal.fields.passportNumber')
            },
            email: {
              label: t('talents:layouts.talentEditModal.fields.email')
            }
          }
        }
      }),
      [i18n.language, t, idNumberIsIdentity, phoneIsIdentity, passportIsIdentity]
    );

    const form = useForm({
      defaultValues: registrationFormValues
    });

    useEffect(() => {
      form.reset(registrationFormValues);
    }, [form, registrationFormValues]);

    const content = (
      <>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={uniqueId}>
            {provided => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: spacing(0.5),
                  width: '100%'
                }}
              >
                {questionsList.map((question, index) => {
                  const currentQuestion = questions?.[index];
                  const questionKey = question.id;

                  if (!currentQuestion) return null;

                  if (!currentQuestion?.isActive) {
                    return null;
                  }

                  return (
                    <TalentRegistrationFormQuestionDraggable
                      key={`${currentQuestion?.id}-${activeLanguage}`}
                      index={index}
                      activeTab={activeTab}
                      activeLanguage={activeLanguage}
                      question={currentQuestion}
                      questionKey={questionKey}
                      onCopyQuestion={onCopyQuestion}
                      onRemoveQuestionRequest={onRemoveQuestionRequest}
                      onAddQuestionOption={onAddQuestionOption}
                      onRemoveQuestionOption={onRemoveQuestionOption}
                    />
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        <ConfirmationModal
          open={!!questionToRemove}
          onConfirm={onQuestionRemoveConfirm}
          onDiscard={onQuestionRemoveCancel}
          title={t('orgSettings.talentRegistrationForm.questionRemoveConfirmation')}
          confirmButtonLabel={t('orgSettings.talentRegistrationForm.removeQuestionAction')}
          illustration={<DeleteQuestionIllustration />}
        />
      </>
    );

    if (activeTab === TALENT_REGISTRATION_FORM_TABS.INTERNAL) {
      return content;
    }

    return (
      <Stack py={2}>
        <CollapsibleContent
          title={t('orgSettings.talentRegistrationForm.sections.registrationForm')}
          tooltip={t('orgSettings.talentRegistrationForm.sections.registrationFormTooltip')}
        >
          <FormProvider {...form}>
            <FieldPrefixProvider prefix="">
              {registrationFormValues.external.map((item, index) => (
                <TalentRegistrationFormQuestion
                  key={`${item.id}-${i18n.language}`}
                  index={index}
                  activeTab={TALENT_REGISTRATION_FORM_TABS.EXTERNAL}
                  activeLanguage={i18n.language}
                  question={item}
                  questionKey={item.id}
                  onCopyQuestion={onCopyQuestion}
                  onRemoveQuestionRequest={onRemoveQuestionRequest}
                  onAddQuestionOption={onAddQuestionOption}
                  onRemoveQuestionOption={onRemoveQuestionOption}
                  disabled
                />
              ))}
            </FieldPrefixProvider>
          </FormProvider>
        </CollapsibleContent>
        <Divider orientation="horizontal" sx={{ borderColor: 'grey.400', my: 2 }} />
        <CollapsibleContent
          title={t('orgSettings.talentRegistrationForm.sections.moreDetails')}
          tooltip={t('orgSettings.talentRegistrationForm.sections.moreDetailsTooltip')}
        >
          {content}
        </CollapsibleContent>
      </Stack>
    );
  }
);

TalentRegistrationFormContent.displayName = 'TalentRegistrationFormContent';

export default TalentRegistrationFormContent;
